import { Send, Loader2 } from 'lucide-react';
import { useEditorStore } from '../../../stores/editor.js';
import type { QualityLevel } from '@agentbrush/shared';

const QUALITY_OPTIONS: { value: QualityLevel; label: string }[] = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Med' },
  { value: 'high', label: 'High' },
];

interface DescriptionInputProps {
  hasMask: boolean;
  onSubmit: () => void;
  /**
   * Token cost per edit. Defaults to 10 (rough average) so tests don't have
   * to import the pricing helpers to render. Real callers compute this via
   * `computeTokenCost(quality, size)` and pass the result.
   */
  cost?: number;
  /** User's current token balance. null hides the "left after" hint. */
  remainingTokens?: number | null;
}

/**
 * Bottom dock — flat layout. Two stacked rows:
 *
 *   Row 1: token-cost meta, flush-left so it aligns with the floating
 *          left toolbar (toolbar is at viewport x=20; the dock wrapper
 *          starts at x=16, so 4px of paddingLeft lands the cost text
 *          right under the toolbar's left edge).
 *   Row 2: a single input field with the Generate button nested inside
 *          its right end — search-bar style.
 *
 * No outer container chrome — the input field carries the only visible
 * surface, so the dock reads as flat against the canvas.
 *
 * Typing in the prompt dismisses the review pill (the implicit "I'm
 * still iterating" signal — see dismissReview in editor.ts).
 */
export function DescriptionInput({
  hasMask,
  onSubmit,
  cost = 10,
  remainingTokens = null,
}: DescriptionInputProps) {
  const description = useEditorStore((s) => s.description);
  const setDescription = useEditorStore((s) => s.setDescription);
  const quality = useEditorStore((s) => s.quality);
  const setQuality = useEditorStore((s) => s.setQuality);
  const submissionStatus = useEditorStore((s) => s.submissionStatus);
  const submissionError = useEditorStore((s) => s.submissionError);
  const sessionFinished = useEditorStore((s) => s.sessionFinished);
  const dismissReview = useEditorStore((s) => s.dismissReview);

  const isSubmitting = submissionStatus === 'submitting';
  const insufficientFunds = remainingTokens !== null && remainingTokens < cost;
  const isDisabled =
    description.trim() === '' || !hasMask || isSubmitting || sessionFinished || insufficientFunds;

  const onDescriptionChange = (next: string) => {
    setDescription(next);
    if (submissionStatus === 'success') {
      dismissReview();
    }
  };

  return (
    <div style={{ width: '100%', position: 'relative' }}>
      {/* Input field with embedded Generate button */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '5px 5px 5px 14px',
          backgroundColor: 'rgba(16, 16, 20, 0.92)',
          backdropFilter: 'blur(12px) saturate(160%)',
          WebkitBackdropFilter: 'blur(12px) saturate(160%)',
          border: '1px solid rgba(255, 255, 255, 0.10)',
          borderRadius: '10px',
          boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.06), 0 12px 36px rgba(0, 0, 0, 0.5)',
        }}
      >
        <span
          aria-hidden="true"
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: '14px',
            color: 'var(--color-text-muted)',
            userSelect: 'none',
          }}
        >
          &gt;
        </span>
        <select
          aria-label="Quality level"
          value={quality}
          disabled={sessionFinished}
          onChange={(e) => setQuality(e.target.value as QualityLevel)}
          style={{
            background: 'transparent',
            border: '1px solid rgba(255, 255, 255, 0.12)',
            borderRadius: '4px',
            color: 'var(--color-text-muted)',
            fontFamily: 'var(--font-mono)',
            fontSize: '11px',
            padding: '2px 4px',
            cursor: sessionFinished ? 'default' : 'pointer',
            opacity: sessionFinished ? 0.5 : 1,
            outline: 0,
            flexShrink: 0,
          }}
        >
          {QUALITY_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <input
          type="text"
          value={description}
          onChange={(e) => onDescriptionChange(e.target.value)}
          placeholder="Describe what should appear in the masked area…"
          maxLength={500}
          aria-label="Edit description"
          disabled={sessionFinished}
          style={{
            flex: 1,
            background: 'transparent',
            border: 0,
            outline: 0,
            color: 'var(--color-text-primary)',
            fontFamily: 'var(--font-sans)',
            fontSize: '14px',
            padding: '6px 0',
            minWidth: 0,
            opacity: sessionFinished ? 0.5 : 1,
          }}
        />
        <button
          type="button"
          onClick={onSubmit}
          disabled={isDisabled}
          aria-label="Submit mask for generation"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '7px',
            padding: '8px 14px',
            backgroundColor: isDisabled ? 'var(--color-border-bright)' : 'var(--color-accent-lime)',
            color: isDisabled ? 'var(--color-text-muted)' : '#0a0a0a',
            border: 0,
            borderRadius: '6px',
            fontFamily: 'var(--font-sans)',
            fontSize: '13px',
            fontWeight: 600,
            cursor: isDisabled ? 'default' : 'pointer',
            height: '34px',
            minWidth: '110px',
            transition: 'background-color 120ms',
          }}
        >
          {isSubmitting ? (
            <Loader2
              size={15}
              style={{ animation: 'spin 1s linear infinite' }}
              aria-hidden="true"
            />
          ) : (
            <>
              <Send size={13} aria-hidden="true" />
              Generate
            </>
          )}
        </button>
      </div>

      {submissionError && (
        <div
          role="alert"
          className="micro-shake"
          style={{
            // Absolute-positioned so the alert floats above the input bar
            // without growing the dock's vertical footprint — keeps the
            // input anchored at bottom: 16 and stops the alert from
            // shoving the review pill up into the input field.
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 'calc(100% + 8px)',
            padding: '10px 18px',
            color: 'var(--color-accent-red, #ef4444)',
            fontFamily: 'var(--font-mono)',
            fontSize: '11px',
            backgroundColor: 'rgba(239, 68, 68, 0.08)',
            backdropFilter: 'blur(12px) saturate(160%)',
            WebkitBackdropFilter: 'blur(12px) saturate(160%)',
            border: '1px solid rgba(239, 68, 68, 0.35)',
            borderRadius: '10px',
            boxShadow: '0 12px 36px rgba(0, 0, 0, 0.5)',
          }}
        >
          {submissionError}
          {' — '}
          <span style={{ color: 'var(--color-text-muted)' }}>
            Try again or adjust your description.
          </span>
        </div>
      )}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes micro-shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-4px); }
          75% { transform: translateX(4px); }
        }
        .micro-shake { animation: micro-shake 0.3s ease-in-out; }
        @media (prefers-reduced-motion: reduce) {
          .micro-shake { animation: none; }
        }
      `}</style>
    </div>
  );
}
